import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { APP_LOCATION } from '@core/config/app-location';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { UserQuery } from '@core/store/user/user.query';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { AuthService, STORAGE_KEYS } from '@zitemedia/zitadel-sso-frontend';
import { V2_ICONS } from '@v2/v2.icons';
import { V2TextSize, V2ThemeService } from '@v2/shared/v2-theme.service';

interface TextSizeChoice {
  value: V2TextSize;
  label: string;
}

@Component({
  selector: 'app-v2-account-panel',
  templateUrl: './v2-account-panel.component.html',
  styleUrls: ['./v2-account-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon, RouterLink],
  providers: [provideIcons(V2_ICONS)],
})
export class V2AccountPanelComponent {
  private readonly userQuery = inject(UserQuery);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly surface = inject(AppSurfaceService);
  private readonly location = inject(APP_LOCATION);
  protected readonly theme = inject(V2ThemeService);

  /**
   * The classic app is the legacy *estimator*, and it is mounted on the
   * estimator host only. From the briefings host this is an absolute URL on the
   * other origin; a `routerLink` there would land on the stray-path redirect
   * (or, before the route table was split by host, render the legacy estimator
   * on the client briefing domain).
   */
  protected readonly classicHref =
    this.surface.current === 'estimator' ? null : this.surface.urlFor('estimator', '/classic');

  readonly open = input<boolean>(false);
  readonly close = output<void>();

  protected readonly user = toSignal(this.userQuery.getUser$, { initialValue: null });

  /**
   * Text size lives here rather than in Settings, where the real client puts
   * it. The reason someone reaches for this control is usually that they
   * cannot comfortably read the page they are on -- which includes the page
   * that would take them to Settings.
   *
   * Each label is set at the size it selects, so the choice shows its effect.
   */
  protected readonly textSizes: TextSizeChoice[] = [
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
  ];

  protected readonly appearance = computed(() =>
    this.theme.theme() === 'dark' ? 'Dark' : 'Light');

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) {
      this.close.emit();
    }
  }

  /**
   * Where the sign-in button sends a signed-out visitor.
   *
   * `null` on the estimator host: sign-in is an ordinary in-app route there,
   * so the template takes a `routerLink` instead -- exactly the branch
   * `classicHref` already uses for the same reason.
   *
   * On the briefings host, sign-in lives on the estimator origin only
   * (`ZITADEL_REDIRECT_URI` is pinned there), so this leaves the origin,
   * carrying a `next` back to here. Same shape as `AuthGuard.sendToLogin`:
   * the return address is built from `APP_LOCATION`, not `Router`, because
   * `sendToLogin` reads the same way and a spec can stub `APP_LOCATION`
   * without breaking TestBed the way stubbing `DOCUMENT` does.
   */
  protected signInHref(): string | null {
    if (this.surface.current === 'estimator') {
      return null;
    }

    const path = `${this.location?.pathname ?? ''}${this.location?.search ?? ''}`;
    const back = `${this.location?.origin ?? ''}${path}`;
    return this.surface.urlFor('estimator', `/login?next=${encodeURIComponent(back)}`);
  }

  /**
   * A successful `logout()` ends in `location.assign` to Zitadel's
   * end_session, which only *starts* a navigation; the promise resolves while
   * it is still in flight. Routing to /login here used to race it: `lib-login`
   * (silentAuth) answers with its own `location.assign` to a `prompt=none`
   * login, and whichever lands first wins. Against a remote Zitadel that was
   * usually the silent login, which cancelled end_session, so the Zitadel
   * session survived and signed the user straight back in. end_session already
   * returns to /login, so only a logout that failed needs to go there.
   *
   * The silent-auth flag is set first so that /login, reached from
   * end_session, shows the sign-in button. Without it `lib-login` tries
   * `prompt=none` again, Zitadel answers `interaction_required` for the
   * signed-out sessions it still lists, and the user lands on Zitadel's
   * account picker instead of our page. `lib-login` clears the flag itself
   * when it shows the button, so the next visit to /login is silent again.
   */
  protected async onLogout(): Promise<void> {
    try {
      sessionStorage.setItem(STORAGE_KEYS.SILENT_AUTH_ATTEMPTED, 'true');
    } catch {
      // Storage blocked: the account picker is the worst case, not a failure.
    }
    try {
      await this.authService.logout();
    } catch {
      this.router.navigateByUrl('login');
    }
  }
}
